import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import {
  CommonModal as Modal,
  Select,
  PrimaryButton
} from "../../Common";
import {
  Grid,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Paper,
  Typography,
  IconButton,
  TableContainer,
} from "@mui/material";
import { Skeleton } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import Routes from "../../../routes";
import UserInfo from "../../../UserInfo";
import { Grey1, Grey2, Grey3, trakrefBlue, White, lightGrayColor } from "../../../color_utils";
import { trakrefFontFamily, trakrefFontFamilySemiBold } from "../../../utils";
import Enums from "../../../enums";
import { Link, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import useNavigate from "../../../hooks/useNavigate";

const SystemMapTable = ({
  assetID = "",
  assetName,
  assetLocationID,
  activeTab,
  initialSubsystemRow,
  activeRow,
  setActiveRow
}) => {
  const [data, setData] = useState([]);
  const [rowState, setRowState] = useState({});
  const [possibleRelatedSystem, setPossibleRelatedSystem] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [modalSubsystemList, setModalSubsystemList] = useState(possibleRelatedSystem);
  const [selectedSystem, setSelectedSystem] = useState({});
  const [subsystemInvalid, setSubsystemInvalid] = useState(false);
  const [selectedSubsystemType, setSelectedSubsystemType] = useState(null);

  const history = useHistory();
  const navigate = useNavigate({
    pageName: 'SystemMapTable'
  });

  const tableCell = {
    borderBottom: "none",
    addingLeft: "3%"
  };

  const fetchData = async (callback) => {
    try {
      // Fetch Data for dropdown
      const getPossibleRelatedSystem =
        await fetch(Routes.Asset.GetPossibleRelationships(assetID, 1));
      const possibleRelatedSystemJson = await getPossibleRelatedSystem.json();

      setPossibleRelatedSystem(possibleRelatedSystemJson);

      // 1. Get all subsystem available
      const response = await fetch(Routes.Asset.GetRelatedAsset(assetID));
      const json = await response.json();

      // Constructing Subsystem data for display here
      // 2. We have fixed compressor rack, evaporator, condensor
      var compressorRackAssets = json.map(function (asset) {
        if (asset.assetTypeId == Enums.AssetTypes.CompressorRack) {
          return {
            id: asset.assetId,
            assetName: asset.name,
            level: 2,
            parentId: Enums.AssetTypes.CompressorRack,
          }
        }
      }).filter(function (element) {
        return element !== undefined;
      });

      var evaporatorAssets = json.map(function (asset) {
        if (asset.assetTypeId == Enums.AssetTypes.Evaporator) {
          return {
            id: asset.assetId,
            assetName: asset.name,
            level: 2,
            parentId: Enums.AssetTypes.Evaporator,
          }
        }
      }).filter(function (element) {
        return element !== undefined;
      });

      var condensorAssets = json.map(function (asset) {
        if (asset.assetTypeId == Enums.AssetTypes.Condensor) {
          return {
            id: asset.assetId,
            assetName: asset.name,
            level: 2,
            parentId: Enums.AssetTypes.Condensor,
          }
        }
      }).filter(function (element) {
        return element !== undefined;
      });

      // 3. Distinguish Type of subsystem's assetType
      const SubsystemList = [
        {
          assetName: "Compressor Rack",
          id: Enums.AssetTypes.CompressorRack,
          level: 1,
          children: compressorRackAssets,
        },
        {
          assetName: "Evaporator",
          id: Enums.AssetTypes.Evaporator,
          children: evaporatorAssets,
          level: 1,
        },
        {
          assetName: "Condensor",
          id: Enums.AssetTypes.Condensor,
          children: condensorAssets,
          level: 1,
        },
      ];

      setData(SubsystemList);
      
      // Open initial subsystem row
      const previousRowState = JSON.parse(sessionStorage.getItem("systemMapState"));
      if (previousRowState) {
        setRowState(previousRowState);
      }
      sessionStorage.removeItem("systemMapState");
      
      if (typeof callback === 'function') {
        callback();
      }
    } catch (error) {
      console.log('Error fetching data', error);
    }
  };

  // TODO: replace route to relate assets
  useEffect(() => {
    fetchData();
  }, []);

  const renderNewSubsystemModal = (row = {}) => {
    var subsystem = [];
    setSelectedSubsystemType(row.id);
    // Distinguish assettype for subsystem dropdown in modal
    switch (row.id) {
      case Enums.AssetTypes.CompressorRack:
        subsystem = possibleRelatedSystem.filter(
          system => system.assetTypeId === Enums.AssetTypes.CompressorRack
        );
        break;
      case Enums.AssetTypes.Evaporator:
        subsystem = possibleRelatedSystem.filter(
          system => system.assetTypeId === Enums.AssetTypes.Evaporator
        );
        break;
      case Enums.AssetTypes.Condensor:
        subsystem = possibleRelatedSystem.filter(
          system => system.assetTypeId === Enums.AssetTypes.Condensor
        );
        break;
      default:
        break;
    }
    if (subsystem) setModalSubsystemList(subsystem);
    return setOpenModal(true);
  };

  // On submit or On cancel, we call this
  const closeModal = () => {
    setOpenModal(false);
    // reset dropdown data
    setSubsystemInvalid(false)
    setSelectedSystem({});
  };


  const pickSubsystem = (e) => {
    const pickedSubsystem = modalSubsystemList.find(system => system.id === e.target.value);
    if (pickedSubsystem) {
      setSubsystemInvalid(false)
      setSelectedSystem(pickedSubsystem);
    } else {
      sessionStorage.setItem("systemMapState", JSON.stringify(rowState));
      navigate.push(`/AddInventory`, {
        state: {
          parentAssetID: assetID,
          parentAssetName: assetName,
          parentAssetLocationID: assetLocationID,
          selectedSubsystemType,
          activeTab: activeTab,
          activeRowState: rowState
        }
      });
    }
  }

  const addSubsystem = async () => {
    if (selectedSystem.id) {
      var body = {
        "AssetRelationshipTypeID": 1,
        "ToAssetID": selectedSystem.id,
        "FromAssetID": assetID
      }
      closeModal();
      const response = await fetch(Routes.Asset.CreateAssetRelationship(), {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      })

      if (response.status === 200) {
        toast.success("The subsystem has been successfully added.", { duration: 4000 });
        return await fetchData(() => {
          setRowState((prevState) => ({
            ...prevState,
            [selectedSystem.assetTypeId]: true
          }));
        });
      } else {
        console.log('Error Response', response);
        return toast.error("Error Saving New Subsystem", { duration: 4000 });
      }
    } else {
      setSubsystemInvalid(true)
    }
  };

  const deleteSubsystem = async (asset) => {
    // Show loading
    console.log("DeleteSubsystem", asset);

    var body = {
      "AssetRelationshipTypeID": 1,
      "ToAssetID": asset.id,
      "FromAssetID": assetID
    }
    const response = await fetch(Routes.Asset.DeleteAssetRelationship(), {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    })

    if (response.status === 200) {
      toast.success("The subsystem has been successfully deleted.", { duration: 4000 });
      return await fetchData();
    } else {
      console.log('Error Response', response);
      return toast.error("Error Deleted Subsystem", { duration: 4000 });
    }
  };

  const RowComponent = ({ data, loading }) => {
    const handleRowOpen = (id) => {
      const newState = { ...rowState, [id]: !rowState[id] };
      setRowState(newState);
      setActiveRow(newState);
    };

    return (
      <>
        {loading
          ? data.map(() => (
            <TableRow>
              <TableCell style={tableCell}>
                <Skeleton />
              </TableCell>
            </TableRow>
          ))
          : data.map((row, index) => {
            const {
              assetName = "",
              children = [],
              level = 1,
              id = "",
              parentId = null,
            } = row;

            const rowColor = index % 2
              ? { background: White }
              : { background: lightGrayColor }

            const isParent = parentId === null;
            const customStyle = {
              marginLeft: `${isParent ? "1.5rem" : `3.5rem`}`,
              color: `${isParent ? Grey2 : trakrefBlue}`,
              fontFamily: trakrefFontFamilySemiBold,
              fontSize: "13px",
              cursor: `${isParent ? "text" : "pointer"}`,
            };
            const canAddOrDeleteSubSystem = !UserInfo.isReadOnly() && (UserInfo.isNativeUser() || UserInfo.isSystemAdministrator());
            return (
              <>
                <TableRow key={id} style={rowColor}>
                  <TableCell style={tableCell}>
                    <Typography
                      variant="subtitle2"
                      style={customStyle}
                      onClick={() => {
                        if (!isParent) {
                          sessionStorage.setItem("systemMapState", JSON.stringify(rowState));
                          navigate.push(`/Inventory/${id}`);
                        }
                      }}
                    >
                      {assetName}
                      {children.length > 0 && (
                        <IconButton
                          aria-label="expand row"
                          size="small"
                          onClick={() => handleRowOpen(id)}
                        >
                          {rowState[id] ? (
                            <ExpandLessIcon style={{ fontSize: 15 }} />
                          ) : (
                            <ExpandMoreIcon style={{ fontSize: 15 }} />
                          )}
                        </IconButton>
                      )}
                    </Typography>
                  </TableCell>

                  <TableCell align="right" style={tableCell}>
                    {canAddOrDeleteSubSystem && (
                      isParent ? (
                        <IconButton
                          aria-label="add"
                          onClick={() => renderNewSubsystemModal(row, true)}
                        >
                          <AddIcon style={{ color: Grey2, fontSize: "28px", fontWeight: "bold" }} />
                        </IconButton>
                      ) : (
                        <IconButton
                          aria-label="delete"
                          onClick={() => deleteSubsystem(row, true)}
                        >
                          <DeleteIcon style={{ color: Grey2, fontSize: "28px", fontWeight: "bold" }} />
                        </IconButton>
                      )
                    )}
                  </TableCell>
                </TableRow>
                {/* Display subsystems */}
                {rowState[id] && (
                  <RowComponent data={children} loading={loading} />
                )}
              </>
            );
          })}
      </>
    );
  };

  return (
    <>
      <TableContainer component={Paper} style={{ marginTop: "2%", width: 'auto' }}>
        <Table aria-label="customized table" size="small" >
          <TableHead>
            <TableRow>
              <TableCell style={{ borderBottom: "none" }} >
                <Typography style={{ marginTop: "1rem", marginLeft: '1.5rem' , fontFamily: trakrefFontFamilySemiBold, color: Grey1, fontSize: "18px" }}>
                  System Map
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={{ borderBottom: "none" }}>
                <Typography style={{ marginLeft: '1.5rem' , fontSize: "10px", color: Grey3, fontFamily: trakrefFontFamilySemiBold }}>
                  SYSTEM NAME
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <RowComponent data={data} />
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Subsystem Modal */}
      <Modal header={`Add Subsystem`} open={openModal} close={closeModal} headerTopMargin={10}>
        <Grid container spacing={2} width={450} >
          <Grid item xs={12} align="center" rowSpacing={12}>
            <Select
              required
              variant='standard'
              label="Subsystem"
              error={subsystemInvalid}
              value={selectedSystem.name}
              onChange={(e) => pickSubsystem(e)}
              options={modalSubsystemList}
              width={80}
              customCreateNewItemText={'Create New Subsystem'}
            />
          </Grid>
          <Grid item xs={12} align="center" marginBottom={5}>
            <PrimaryButton
              fullWidth
              useLargeButton
              buttonText="Add Subsystem"
              onClick={addSubsystem}
              width={80}
            />
          </Grid>
        </Grid>
      </Modal>
    </>
  );
};

SystemMapTable.propTypes = {
  assetID: PropTypes.string.isRequired,
  assetName: PropTypes.string.isRequired,
  assetLocationID: PropTypes.number.isRequired
};

export default SystemMapTable;
